/**
 * Cached Mango query compiler with template-based caching.
 *
 * This module provides the main mangoCompile function with automatic caching.
 * It uses template-based caching where the query structure is normalized
 * (values extracted), allowing the same compiled logic to be reused with
 * different parameter values.
 *
 * For example, queries like:
 * - `{ type: "post", status: "published" }`
 * - `{ type: "page", status: "draft" }`
 *
 * Share the same template structure `{ type: $0, status: $1 }` and can reuse
 * the same compiled predicate with different bound values.
 */

import type { MangoSelector, Predicate } from "./MangoTypes";
import { normalizeSelector, generateTemplateKey } from "./templateNormalize";
import {
    compileTemplateSelector,
    type ParameterizedPredicate,
} from "./compileTemplateSelector";
import {
    cacheGet,
    cacheSet,
    clearCacheByPrefix,
    getCacheStats,
    CACHE_PREFIX_TEMPLATE,
    scheduleTemplatePersist,
    getPersistedTemplates,
    setWarmingFlag,
} from "./queryCache";

// Re-export Predicate type for convenience
export type { Predicate } from "./MangoTypes";

// ============================================================================
// Cache Management Exports
// ============================================================================

/**
 * Clear all cached compiled queries.
 * Does not affect mangoToDexie cache.
 * For clearing all Mango caches, use clearAllMangoCache() from queryCache.
 */
export function clearMangoCache(): void {
    clearCacheByPrefix(CACHE_PREFIX_TEMPLATE);
}

/**
 * Get cache statistics for mangoCompile.
 */
export function getMangoCacheStats(): { size: number; keys: string[] } {
    return getCacheStats(CACHE_PREFIX_TEMPLATE);
}

// ============================================================================
// Main Export
// ============================================================================

/**
 * Compile a Mango query into a filter function.
 *
 * Uses template-based caching: the query structure is normalized by extracting
 * values, and the compiled predicate is cached based on the structure alone.
 * This allows queries with the same structure but different values to share
 * the same compiled logic.
 *
 * For example:
 * - `{ type: "post", status: "published" }`
 * - `{ type: "page", status: "draft" }`
 *
 * Both normalize to the same template structure and share the compiled predicate.
 * Only the values are bound differently at runtime.
 *
 * @param q - The Mango query selector to compile
 * @returns A predicate function that returns true if a document matches the query
 */
export function mangoCompile(q: MangoSelector): Predicate {
    // Handle invalid query (not cacheable)
    if (q === null || typeof q !== "object") {
        return () => false;
    }

    // Handle empty query (not cacheable, just return constant).
    // Uses for...in with break instead of Object.keys().length to avoid
    // array allocation on this hot path (runs per filter invocation).
    let hasKeys = false;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const _key in q) {
        hasKeys = true;
        break;
    }
    if (!hasKeys) {
        return () => true;
    }

    // Normalize the selector into a template and extract values
    const { template, values } = normalizeSelector(q);

    // Generate cache key from template structure (not values)
    const cacheKey = generateTemplateKey(template, CACHE_PREFIX_TEMPLATE);

    // Check cache for compiled template
    const cached = cacheGet<ParameterizedPredicate>(cacheKey);
    
    if (cached) {
        // Cache hit - return bound predicate with cached template
        return (doc: any) => cached(doc, values);
    }

    // Cache miss - compile the template and cache it
    const compiledTemplate = compileTemplateSelector(template);
    cacheSet(cacheKey, compiledTemplate);
    scheduleTemplatePersist(cacheKey, template);

    // Return a bound predicate that captures the extracted values
    // This is a cheap closure creation - the expensive compilation is cached
    return (doc: any) => compiledTemplate(doc, values);
}

/**
 * Pre-warm the mangoCompile cache from templates persisted in localStorage.
 *
 * Call this early in app startup (before components mount) to eliminate
 * cold-start compilation latency. On the first visit the cache will be
 * empty; templates are automatically persisted as queries execute.
 * On subsequent page loads, this function restores and compiles them
 * so the first real query hits a warm cache.
 */
export function warmMangoCompileCache(): void {
    setWarmingFlag(true);
    try {
        const persisted = getPersistedTemplates(CACHE_PREFIX_TEMPLATE);
        persisted.forEach((template, key) => {
            if (!cacheGet(key)) {
                const compiled = compileTemplateSelector(template as MangoSelector);
                cacheSet(key, compiled);
            }
        });
    } finally {
        setWarmingFlag(false);
    }
}
